import {
  ROOM_IMAGE_SLOTS,
  type AlbumDocument,
  type FurnitureGeometry,
  type RoomFurnitureLayout,
  type RoomFurnitureSlot,
  type RoomImageSlot,
} from "../lib/types.ts";
import * as db from "../storage/indexeddb.ts";
import * as opfs from "../storage/opfs.ts";
import { importPhotoFile } from "../storage/assets.ts";
import { setPendingImportFile } from "../pending-import.ts";
import { router } from "../router.ts";
import { store } from "../store.ts";
import {
  ALBUM_SPINE,
  DEFAULT_ROOM_FURNITURE_LAYOUT,
  FURNITURE,
  FurnitureKind,
  ROOM_BACKGROUND,
  applyFurnitureLayout,
  moveFurnitureGeometry,
  normalizeRoomFurnitureLayout,
  resizeFurnitureGeometry,
  resizeFurnitureGeometryBy,
  type FurnitureSpec,
} from "../room/furniture.ts";

const SHELF_SLOT_COUNT = 13;
const FURNITURE_IMAGE_MIME_TYPES = ["image/png", "image/webp"] as const;
const FURNITURE_DRAG_THRESHOLD_PX = 4;

const FURNITURE_BY_ROOM_SLOT: Record<Exclude<RoomImageSlot, "room">, FurnitureKind> = {
  shelf: FurnitureKind.Shelf,
  fireplace: FurnitureKind.Fireplace,
  table: FurnitureKind.Table,
};

interface ResolvedRoomImage {
  src: string;
  customized: boolean;
}

function roomSlotLabel(slot: RoomImageSlot): string {
  return slot[0]!.toUpperCase() + slot.slice(1);
}

function defaultRoomImage(slot: RoomImageSlot): string {
  if (slot === "room") return ROOM_BACKGROUND;
  return FURNITURE[FURNITURE_BY_ROOM_SLOT[slot]].src;
}

async function resolveRoomImage(slot: RoomImageSlot): Promise<ResolvedRoomImage> {
  const fallback = defaultRoomImage(slot);
  const assetId = store.settings?.roomImages?.[slot];
  if (!assetId) return { src: fallback, customized: false };

  const asset = await db.getAsset(assetId);
  if (!asset || asset.status !== "ready") return { src: fallback, customized: false };
  const src = await opfs.getObjectUrl(asset.variants.display.opfsPath);
  return src ? { src, customized: true } : { src: fallback, customized: false };
}

/**
 * Selection order is intentionally different from shelf order. Taking the first
 * N entries yields stable, scattered filler slots as real albums are added.
 */
const FILLER_BOOKS = [
  { slot: 1, title: "Field Notes" },
  { slot: 10, title: "Quiet Hours" },
  { slot: 5, title: "Blue Atlas" },
  { slot: 8, title: "Small Wonders" },
  { slot: 3, title: "Sunday Light" },
  { slot: 12, title: "Old Roads" },
  { slot: 6, title: "Wild Garden" },
  { slot: 0, title: "Night Train" },
  { slot: 9, title: "Paper Moons" },
  { slot: 4, title: "Sea Glass" },
  { slot: 11, title: "Homeward" },
  { slot: 2, title: "Golden Days" },
  { slot: 7, title: "After Rain" },
] as const;

type ShelfItem =
  | { kind: "album"; album: AlbumDocument }
  | { kind: "filler"; title: string };

function spineHue(title: string): string {
  let hash = 0;
  for (let i = 0; i < title.length; i++) hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
  const hue = hash % 40;
  return `hsl(${25 + hue} 38% ${28 + (hash % 18)}%)`;
}

function composeShelf(albums: AlbumDocument[]): ShelfItem[] {
  if (albums.length >= SHELF_SLOT_COUNT) {
    return albums.map((album) => ({ kind: "album", album }));
  }

  const fillerCount = SHELF_SLOT_COUNT - albums.length;
  const fillersBySlot = new Map<number, string>(
    FILLER_BOOKS.slice(0, fillerCount).map((book) => [book.slot, book.title]),
  );
  let albumIndex = 0;

  return Array.from({ length: SHELF_SLOT_COUNT }, (_, slot): ShelfItem => {
    const fillerTitle = fillersBySlot.get(slot);
    if (fillerTitle !== undefined) return { kind: "filler", title: fillerTitle };

    const album = albums[albumIndex++];
    if (!album) throw new Error("Shelf composition is missing an album");
    return { kind: "album", album };
  });
}

async function coverAccent(album: AlbumDocument): Promise<string | null> {
  if (!album.coverAssetId) return null;
  const record = await db.getAsset(album.coverAssetId);
  if (!record) return null;
  return opfs.getObjectUrl(record.variants.thumbnail.opfsPath);
}

function prefersReducedMotion(): boolean {
  return (
    Boolean(store.settings?.reducedMotion) ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function isCoarsePointer(): boolean {
  return window.matchMedia("(pointer: coarse)").matches;
}

function createFurnitureImage(
  spec: FurnitureSpec,
  image: ResolvedRoomImage,
): HTMLImageElement {
  const img = document.createElement("img");
  img.className = "furniture-photo";
  img.src = image.src;
  img.alt = "";
  img.draggable = false;
  img.setAttribute("aria-hidden", "true");
  img.dataset.roomImageSlot = spec.kind;
  img.dataset.customRoomImage = String(image.customized);
  return img;
}

function createFurnitureElement(
  spec: FurnitureSpec,
  image: ResolvedRoomImage,
  geometry: FurnitureGeometry,
): HTMLElement {
  const el = document.createElement("div");
  el.className = "furniture";
  applyFurnitureLayout(el, spec, geometry);
  el.setAttribute("aria-hidden", "true");
  el.appendChild(createFurnitureImage(spec, image));
  return el;
}

function furnitureLayoutIsDefault(layout: RoomFurnitureLayout): boolean {
  return (Object.values(FurnitureKind) as RoomFurnitureSlot[]).every((slot) => {
    const actual = layout[slot];
    const expected = DEFAULT_ROOM_FURNITURE_LAYOUT[slot];
    return (
      Math.abs(actual.x - expected.x) < 1e-8 &&
      Math.abs(actual.y - expected.y) < 1e-8 &&
      Math.abs(actual.width - expected.width) < 1e-8
    );
  });
}

function applyShelfRailLayout(rail: HTMLElement, geometry: FurnitureGeometry): void {
  const scale = geometry.width / DEFAULT_ROOM_FURNITURE_LAYOUT.shelf.width;
  rail.style.width = `${geometry.width * 100}%`;
  rail.style.left = `${geometry.x * 100}%`;
  rail.style.bottom = `${(1 - geometry.y) * 100}%`;
  rail.style.height = `${ALBUM_SPINE.railHeightPct * scale}%`;
  rail.style.gap = `${ALBUM_SPINE.gapCqw * scale}cqw`;
  rail.style.transform = "";
  rail.querySelectorAll<HTMLElement>(".room-album").forEach((item) => sizeSpine(item, scale));
}

function createChromeButton(label: string, text: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "room-chrome-btn";
  button.textContent = text;
  button.setAttribute("aria-label", label);
  button.title = label;
  return button;
}

function createRoomChrome(customizing: boolean, busy: boolean): {
  chrome: HTMLElement;
  newAlbumBtn: HTMLButtonElement | null;
  settingsBtn: HTMLButtonElement | null;
  importBtn: HTMLButtonElement | null;
  importInput: HTMLInputElement | null;
  customizeBtn: HTMLButtonElement | null;
  doneBtn: HTMLButtonElement | null;
  resetAllBtn: HTMLButtonElement | null;
  resetLayoutBtn: HTMLButtonElement | null;
} {
  const chrome = document.createElement("div");
  chrome.className = "room-chrome";

  if (customizing) {
    const resetAllBtn = createChromeButton("Reset all room photos", "↺");
    resetAllBtn.disabled = busy;
    const resetLayoutBtn = createChromeButton("Reset furniture layout", "⌂");
    resetLayoutBtn.disabled = busy;
    const doneBtn = createChromeButton("Done customizing", "✓");
    doneBtn.disabled = busy;
    chrome.append(resetAllBtn, resetLayoutBtn, doneBtn);
    return {
      chrome,
      newAlbumBtn: null,
      settingsBtn: null,
      importBtn: null,
      importInput: null,
      customizeBtn: null,
      doneBtn,
      resetAllBtn,
      resetLayoutBtn,
    };
  }

  const newAlbumBtn = createChromeButton("New Album", "+");
  const settingsBtn = createChromeButton("Settings", "⚙");

  const importBtn = createChromeButton("Import photo", "↑");

  const importInput = document.createElement("input");
  importInput.type = "file";
  importInput.accept = "image/*";
  importInput.hidden = true;
  importInput.dataset.action = "room-import";

  const customizeBtn = createChromeButton("Customize room", "✎");
  chrome.append(newAlbumBtn, settingsBtn, importBtn, importInput, customizeBtn);
  return {
    chrome,
    newAlbumBtn,
    settingsBtn,
    importBtn,
    importInput,
    customizeBtn,
    doneBtn: null,
    resetAllBtn: null,
    resetLayoutBtn: null,
  };
}

interface RoomCustomizationTarget {
  slot: RoomImageSlot;
  element: HTMLElement;
  replaceBtn: HTMLButtonElement;
  restoreBtn: HTMLButtonElement | null;
  resizeHandle: HTMLButtonElement | null;
  input: HTMLInputElement;
}

function createCustomizationTarget(
  slot: RoomImageSlot,
  configured: boolean,
  busy: boolean,
  geometry?: FurnitureGeometry,
): RoomCustomizationTarget {
  const target = document.createElement("div");
  target.className = "room-customization-target";
  target.dataset.roomCustomizationSlot = slot;

  if (slot === "room") {
    target.style.zIndex = "10";
  } else {
    const spec = FURNITURE[FURNITURE_BY_ROOM_SLOT[slot]];
    applyFurnitureLayout(target, spec, geometry);
    delete target.dataset.furniture;
    target.style.zIndex = String(10 + spec.zIndex);
  }

  const replaceBtn = document.createElement("button");
  replaceBtn.type = "button";
  replaceBtn.className = "room-customization-replace";
  replaceBtn.disabled = busy;
  replaceBtn.setAttribute("aria-label", `Replace ${slot} photo`);
  const replaceLabel = document.createElement("span");
  replaceLabel.className = "room-customization-label";
  replaceLabel.textContent = roomSlotLabel(slot);
  replaceBtn.appendChild(replaceLabel);

  let resizeHandle: HTMLButtonElement | null = null;
  if (slot !== "room") {
    replaceBtn.classList.add("room-furniture-drag-surface");
    replaceBtn.title = `Drag to move ${slot}`;
    resizeHandle = document.createElement("button");
    resizeHandle.type = "button";
    resizeHandle.className = "room-furniture-resize";
    resizeHandle.dataset.handle = "resize-se";
    resizeHandle.disabled = busy;
    resizeHandle.setAttribute("aria-label", `Resize ${slot}`);
    resizeHandle.title = `Resize ${slot}`;
  }

  const input = document.createElement("input");
  input.type = "file";
  input.accept = slot === "room" ? "image/*" : FURNITURE_IMAGE_MIME_TYPES.join(",");
  input.hidden = true;
  input.disabled = busy;
  input.dataset.roomImageSlot = slot;

  let restoreBtn: HTMLButtonElement | null = null;
  target.append(replaceBtn, input);
  if (resizeHandle) target.appendChild(resizeHandle);
  if (configured) {
    restoreBtn = document.createElement("button");
    restoreBtn.type = "button";
    restoreBtn.className = "room-customization-restore";
    restoreBtn.disabled = busy;
    restoreBtn.textContent = "Restore";
    restoreBtn.setAttribute("aria-label", `Restore default ${slot} photo`);
    target.appendChild(restoreBtn);
  }

  return { slot, element: target, replaceBtn, restoreBtn, resizeHandle, input };
}

function openAlbum(albumId: string, spine: HTMLElement, room: HTMLElement): void {
  if (isCoarsePointer() || prefersReducedMotion()) {
    router.navigate({ name: "viewer", albumId, page: 0 });
    return;
  }

  const table = room.querySelector(`[data-furniture="${FurnitureKind.Table}"]`) as HTMLElement | null;
  if (!table) {
    router.navigate({ name: "viewer", albumId, page: 0 });
    return;
  }

  const spineRect = spine.getBoundingClientRect();
  const tableRect = table.getBoundingClientRect();
  const ghost = spine.cloneNode(true) as HTMLElement;
  ghost.className = "room-album room-album-ghost";
  ghost.style.left = `${spineRect.left}px`;
  ghost.style.top = `${spineRect.top}px`;
  ghost.style.width = `${spineRect.width}px`;
  ghost.style.height = `${spineRect.height}px`;
  document.body.appendChild(ghost);
  spine.setAttribute("aria-hidden", "true");
  spine.style.visibility = "hidden";

  const animation = ghost.animate(
    [
      {
        left: `${spineRect.left}px`,
        top: `${spineRect.top}px`,
        width: `${spineRect.width}px`,
        height: `${spineRect.height}px`,
        transform: "rotateZ(0deg) scale(1)",
        opacity: 1,
      },
      {
        left: `${tableRect.left + tableRect.width / 2 - 40}px`,
        top: `${tableRect.top + tableRect.height / 2 - 60}px`,
        width: `80px`,
        height: `110px`,
        transform: "rotateZ(-8deg) scale(1.15)",
        opacity: 1,
      },
    ],
    { duration: 650, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "forwards" },
  );

  animation.onfinish = () => {
    ghost.remove();
    spine.style.visibility = "";
    spine.removeAttribute("aria-hidden");
    router.navigate({ name: "viewer", albumId, page: 0 });
  };
}

function createSpineFace(titleText: string): HTMLSpanElement {
  const face = document.createElement("span");
  face.className = "room-album-spine-face";
  face.style.background = spineHue(titleText);

  const title = document.createElement("span");
  title.className = "room-album-spine-title";
  title.textContent = titleText;
  face.appendChild(title);
  return face;
}

function sizeSpine(item: HTMLElement, scale = 1): void {
  item.style.width = `${ALBUM_SPINE.widthCqw * scale}cqw`;
  item.style.height = `${ALBUM_SPINE.heightCqh * scale}cqh`;
}

async function createAlbumSpine(album: AlbumDocument): Promise<HTMLButtonElement> {
  const item = document.createElement("button");
  item.type = "button";
  item.className = "room-album room-album-interactive";
  item.setAttribute("role", "listitem");
  item.dataset.albumId = album.id;
  item.setAttribute("aria-label", `Open album ${album.title}`);
  sizeSpine(item);

  const face = createSpineFace(album.title);
  const accent = await coverAccent(album);
  if (accent) {
    face.style.setProperty("--spine-accent", `url("${accent}")`);
    face.classList.add("has-accent");
  }

  item.appendChild(face);
  return item;
}

function createFillerSpine(title: string): HTMLSpanElement {
  const item = document.createElement("span");
  item.className = "room-album room-album-filler";
  item.setAttribute("aria-hidden", "true");
  sizeSpine(item);
  item.appendChild(createSpineFace(title));
  return item;
}

export function renderBookshelf(root: HTMLElement): () => void {
  let disposed = false;
  let customizing = false;
  let customizationBusy: string | null = null;
  let customizationMessage: { text: string; error: boolean } | null = null;
  let focusAfterPaint: string | null = null;
  let paintGeneration = 0;

  function repaint(focusSelector?: string): void {
    if (focusSelector) focusAfterPaint = focusSelector;
    void paint();
  }

  function beginCustomizationOperation(message: string): void {
    customizationBusy = message;
    customizationMessage = null;
    const status = root.querySelector<HTMLElement>("[data-room-customization-status]");
    status?.focus({ preventScroll: true });
    root
      .querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        ".room-customization-target button, .room-customization-target input, .room-chrome button",
      )
      .forEach((control) => (control.disabled = true));
    repaint("[data-room-customization-status]");
  }

  async function paint(): Promise<void> {
    if (disposed) return;
    const generation = ++paintGeneration;
    const imageEntries = await Promise.all(
      ROOM_IMAGE_SLOTS.map(async (slot) => [slot, await resolveRoomImage(slot)] as const),
    );
    if (disposed || generation !== paintGeneration) return;
    const roomImages = Object.fromEntries(imageEntries) as Record<
      RoomImageSlot,
      ResolvedRoomImage
    >;
    const furnitureLayout = normalizeRoomFurnitureLayout(store.settings?.roomFurnitureLayout);

    const room = document.createElement("div");
    room.className = `living-room${customizing ? " room-customizing" : ""}`;
    room.setAttribute("role", "main");
    room.setAttribute("aria-label", "Cozy living room");

    const stage = document.createElement("div");
    stage.className = "room-stage";

    const bg = document.createElement("img");
    bg.className = "room-bg";
    bg.src = roomImages.room.src;
    bg.alt = "";
    bg.draggable = false;
    bg.setAttribute("aria-hidden", "true");
    bg.dataset.roomImageSlot = "room";
    bg.dataset.customRoomImage = String(roomImages.room.customized);

    const pieceSpecs = [
      FURNITURE[FurnitureKind.Fireplace],
      FURNITURE[FurnitureKind.Shelf],
      FURNITURE[FurnitureKind.Table],
    ];
    const pieces = pieceSpecs.map((spec) =>
      createFurnitureElement(spec, roomImages[spec.kind], furnitureLayout[spec.kind]),
    );

    const {
      chrome,
      newAlbumBtn,
      settingsBtn,
      importBtn,
      importInput: fileInput,
      customizeBtn,
      doneBtn,
      resetAllBtn,
      resetLayoutBtn,
    } = createRoomChrome(customizing, customizationBusy !== null);

    const shelfRail = document.createElement("section");
    shelfRail.className = "album-shelf-rail";
    shelfRail.setAttribute("role", "list");
    shelfRail.setAttribute("aria-label", "Album shelf");
    const shelfSpec = FURNITURE[FurnitureKind.Shelf];
    shelfRail.style.zIndex = String(shelfSpec.zIndex + 1);

    const visibleAlbums = store.loading || store.error ? [] : store.albums;
    for (const item of composeShelf(visibleAlbums)) {
      shelfRail.appendChild(
        item.kind === "album" ? await createAlbumSpine(item.album) : createFillerSpine(item.title),
      );
    }
    applyShelfRailLayout(shelfRail, furnitureLayout.shelf);
    if (disposed || generation !== paintGeneration) return;

    if (customizing) {
      shelfRail
        .querySelectorAll<HTMLButtonElement>(".room-album-interactive")
        .forEach((button) => (button.disabled = true));
    }

    if (store.loading) {
      const loading = document.createElement("p");
      loading.className = "room-status";
      loading.textContent = "Lighting the room…";
      stage.append(bg, ...pieces, shelfRail, loading);
      room.append(stage, chrome);
      root.replaceChildren(room);
      return;
    }

    if (store.error) {
      const err = document.createElement("p");
      err.className = "room-status error";
      err.textContent = store.error;
      stage.append(bg, ...pieces, shelfRail, err);
      room.append(stage, chrome);
      root.replaceChildren(room);
      return;
    }

    const customizationTargets = customizing
      ? ROOM_IMAGE_SLOTS.map((slot) =>
          createCustomizationTarget(
            slot,
            Boolean(store.settings?.roomImages?.[slot]),
            customizationBusy !== null,
            slot === "room" ? undefined : furnitureLayout[slot],
          ),
        )
      : [];

    stage.append(bg, ...pieces, shelfRail, ...customizationTargets.map((target) => target.element));

    let customizationStatus: HTMLParagraphElement | null = null;
    if (customizing) {
      customizationStatus = document.createElement("p");
      customizationStatus.className = `room-customization-status${customizationMessage?.error ? " error" : ""}`;
      customizationStatus.dataset.roomCustomizationStatus = "";
      customizationStatus.tabIndex = -1;
      customizationStatus.setAttribute("role", customizationMessage?.error ? "alert" : "status");
      customizationStatus.setAttribute(
        "aria-live",
        customizationMessage?.error ? "assertive" : "polite",
      );
      customizationStatus.textContent =
        customizationBusy ?? customizationMessage?.text ?? "Choose a room section";
    }

    room.append(stage, chrome);
    if (customizationStatus) room.appendChild(customizationStatus);
    if (disposed || generation !== paintGeneration) return;
    root.replaceChildren(room);

    const selector = focusAfterPaint;
    focusAfterPaint = null;
    if (selector) {
      queueMicrotask(() => root.querySelector<HTMLElement>(selector)?.focus());
    }

    if (!customizing) {
      newAlbumBtn?.addEventListener("click", async () => {
        const album = await store.createAlbum();
        router.navigate({ name: "editor", albumId: album.id, page: 0 });
      });

      settingsBtn?.addEventListener("click", () => router.navigate({ name: "settings" }));

      importBtn?.addEventListener("click", () => fileInput?.click());

      fileInput?.addEventListener("change", async () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        setPendingImportFile(file);
        const album = await store.createAlbum();
        router.navigate({ name: "editor", albumId: album.id, page: 0 });
      });

      customizeBtn?.addEventListener("click", () => {
        customizing = true;
        customizationMessage = null;
        repaint('[aria-label="Replace room photo"]');
      });

      shelfRail.querySelectorAll(".room-album-interactive[data-album-id]").forEach((el) => {
        const button = el as HTMLButtonElement;
        button.addEventListener("click", () => {
          const id = button.dataset.albumId;
          if (id) openAlbum(id, button, room);
        });
      });
      return;
    }

    doneBtn?.addEventListener("click", () => {
      customizing = false;
      customizationMessage = null;
      repaint('[aria-label="Customize room"]');
    });

    if (resetAllBtn) {
      resetAllBtn.disabled =
        customizationBusy !== null ||
        !ROOM_IMAGE_SLOTS.some((slot) => Boolean(store.settings?.roomImages?.[slot]));
      resetAllBtn.addEventListener("click", async () => {
        if (!window.confirm("Restore all room photos to their defaults?")) return;
        beginCustomizationOperation("Restoring all room photos…");
        try {
          await store.restoreAllRoomImages();
          customizationMessage = { text: "All room photos restored.", error: false };
        } catch (error) {
          customizationMessage = {
            text: error instanceof Error ? error.message : "Could not restore room photos",
            error: true,
          };
        } finally {
          customizationBusy = null;
          repaint('[aria-label="Done customizing"]');
        }
      });
    }

    if (resetLayoutBtn) {
      resetLayoutBtn.disabled =
        customizationBusy !== null || furnitureLayoutIsDefault(furnitureLayout);
      resetLayoutBtn.addEventListener("click", async () => {
        if (!window.confirm("Restore the default furniture layout?")) return;
        beginCustomizationOperation("Restoring furniture layout…");
        try {
          await store.restoreRoomFurnitureLayout();
          customizationMessage = { text: "Furniture layout restored.", error: false };
        } catch (error) {
          customizationMessage = {
            text: error instanceof Error ? error.message : "Could not restore furniture layout",
            error: true,
          };
        } finally {
          customizationBusy = null;
          repaint('[aria-label="Done customizing"]');
        }
      });
    }

    let activeFurnitureGesture:
      | {
          slot: RoomFurnitureSlot;
          mode: "move" | "resize";
          pointerId: number;
          startClientX: number;
          startClientY: number;
          startGeometry: FurnitureGeometry;
          nextGeometry: FurnitureGeometry;
          stageRect: DOMRect;
          control: HTMLElement;
          target: HTMLElement;
          moved: boolean;
        }
      | null = null;

    const previewFurnitureGeometry = (
      slot: RoomFurnitureSlot,
      geometry: FurnitureGeometry,
      target: HTMLElement,
    ) => {
      const spec = FURNITURE[slot as FurnitureKind];
      const piece = stage.querySelector<HTMLElement>(`.furniture[data-furniture="${slot}"]`);
      if (piece) applyFurnitureLayout(piece, spec, geometry);
      applyFurnitureLayout(target, spec, geometry);
      delete target.dataset.furniture;
      target.style.zIndex = String(10 + spec.zIndex);
      if (slot === FurnitureKind.Shelf) applyShelfRailLayout(shelfRail, geometry);
    };

    const persistFurnitureGeometry = (
      slot: RoomFurnitureSlot,
      geometry: FurnitureGeometry,
      target: HTMLElement,
      focusSelector?: string,
    ) => {
      furnitureLayout[slot] = geometry;
      previewFurnitureGeometry(slot, geometry, target);
      if (focusSelector) focusAfterPaint = focusSelector;
      void store.setRoomFurnitureGeometry(slot, geometry).catch((error) => {
        customizationMessage = {
          text: error instanceof Error ? error.message : `Could not save ${slot} layout`,
          error: true,
        };
        repaint(`[aria-label="Replace ${slot} photo"]`);
      });
    };

    for (const target of customizationTargets) {
      const { slot, element, replaceBtn, restoreBtn, resizeHandle, input } = target;
      let suppressNextReplaceClick = false;
      replaceBtn.addEventListener("click", (event) => {
        if (suppressNextReplaceClick) {
          suppressNextReplaceClick = false;
          event.preventDefault();
          return;
        }
        input.click();
      });

      if (slot !== "room" && resizeHandle) {
        const furnitureSlot = slot;

        const bindPointerGesture = (
          control: HTMLElement,
          mode: "move" | "resize",
        ) => {
          control.addEventListener("pointerdown", (event) => {
            if (event.button !== 0 || activeFurnitureGesture) return;
            const startGeometry = { ...furnitureLayout[furnitureSlot] };
            activeFurnitureGesture = {
              slot: furnitureSlot,
              mode,
              pointerId: event.pointerId,
              startClientX: event.clientX,
              startClientY: event.clientY,
              startGeometry,
              nextGeometry: startGeometry,
              stageRect: stage.getBoundingClientRect(),
              control,
              target: element,
              moved: false,
            };
            element.classList.add("room-furniture-gesturing");
            control.setPointerCapture(event.pointerId);
          });

          control.addEventListener("pointermove", (event) => {
            const gesture = activeFurnitureGesture;
            if (!gesture || gesture.control !== control || gesture.pointerId !== event.pointerId) {
              return;
            }
            const dx = event.clientX - gesture.startClientX;
            const dy = event.clientY - gesture.startClientY;
            if (!gesture.moved && Math.hypot(dx, dy) < FURNITURE_DRAG_THRESHOLD_PX) return;
            gesture.moved = true;
            event.preventDefault();

            if (gesture.mode === "move") {
              gesture.nextGeometry = moveFurnitureGeometry(
                gesture.slot,
                gesture.startGeometry,
                dx / gesture.stageRect.width,
                dy / gesture.stageRect.height,
              );
            } else {
              const anchorX =
                gesture.stageRect.left + gesture.startGeometry.x * gesture.stageRect.width;
              const anchorY =
                gesture.stageRect.top + gesture.startGeometry.y * gesture.stageRect.height;
              gesture.nextGeometry = resizeFurnitureGeometry(
                gesture.slot,
                gesture.startGeometry,
                event.clientX - anchorX,
                event.clientY - anchorY,
                gesture.stageRect.width,
              );
            }
            previewFurnitureGeometry(gesture.slot, gesture.nextGeometry, gesture.target);
          });

          const finishGesture = (commit: boolean) => {
            const gesture = activeFurnitureGesture;
            if (!gesture || gesture.control !== control) return;
            activeFurnitureGesture = null;
            gesture.target.classList.remove("room-furniture-gesturing");
            if (commit && gesture.moved) {
              if (gesture.mode === "move") suppressNextReplaceClick = true;
              persistFurnitureGeometry(gesture.slot, gesture.nextGeometry, gesture.target);
            } else if (gesture.moved) {
              previewFurnitureGeometry(gesture.slot, gesture.startGeometry, gesture.target);
            }
          };

          control.addEventListener("pointerup", (event) => {
            if (activeFurnitureGesture?.pointerId !== event.pointerId) return;
            finishGesture(true);
          });
          control.addEventListener("pointercancel", (event) => {
            if (activeFurnitureGesture?.pointerId !== event.pointerId) return;
            finishGesture(false);
          });
          control.addEventListener("lostpointercapture", () => finishGesture(true));
        };

        bindPointerGesture(replaceBtn, "move");
        bindPointerGesture(resizeHandle, "resize");

        replaceBtn.addEventListener("keydown", (event) => {
          if (!event.key.startsWith("Arrow")) return;
          event.preventDefault();
          const step = event.shiftKey ? 0.05 : 0.01;
          const dx = event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0;
          const dy = event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
          persistFurnitureGeometry(
            furnitureSlot,
            moveFurnitureGeometry(furnitureSlot, furnitureLayout[furnitureSlot], dx, dy),
            element,
            `[aria-label="Replace ${furnitureSlot} photo"]`,
          );
        });

        resizeHandle.addEventListener("keydown", (event) => {
          if (!event.key.startsWith("Arrow")) return;
          event.preventDefault();
          const step = event.shiftKey ? 0.05 : 0.01;
          const direction =
            event.key === "ArrowLeft" || event.key === "ArrowUp" ? -step : step;
          persistFurnitureGeometry(
            furnitureSlot,
            resizeFurnitureGeometryBy(
              furnitureSlot,
              furnitureLayout[furnitureSlot],
              direction,
            ),
            element,
            `[aria-label="Resize ${furnitureSlot}"]`,
          );
        });
      }

      input.addEventListener("change", async () => {
        const file = input.files?.[0];
        if (!file) return;
        beginCustomizationOperation(`Uploading ${slot} photo…`);
        try {
          const asset = await importPhotoFile(
            file,
            slot === "room"
              ? undefined
              : {
                  allowedMimeTypes: FURNITURE_IMAGE_MIME_TYPES,
                  requireTransparency: true,
                },
          );
          await store.setRoomImage(slot, asset.id);
          customizationMessage = {
            text: `${roomSlotLabel(slot)} photo updated.`,
            error: false,
          };
        } catch (error) {
          customizationMessage = {
            text: error instanceof Error ? error.message : `Could not update ${slot} photo`,
            error: true,
          };
        } finally {
          input.value = "";
          customizationBusy = null;
          repaint(`[aria-label="Replace ${slot} photo"]`);
        }
      });

      restoreBtn?.addEventListener("click", async () => {
        beginCustomizationOperation(`Restoring ${slot} photo…`);
        try {
          await store.restoreRoomImage(slot);
          customizationMessage = {
            text: `${roomSlotLabel(slot)} photo restored.`,
            error: false,
          };
        } catch (error) {
          customizationMessage = {
            text: error instanceof Error ? error.message : `Could not restore ${slot} photo`,
            error: true,
          };
        } finally {
          customizationBusy = null;
          repaint(`[aria-label="Replace ${slot} photo"]`);
        }
      });
    }
  }

  const unsub = store.subscribe(() => void paint());
  void paint();

  return () => {
    disposed = true;
    paintGeneration += 1;
    unsub();
  };
}
